import { getSupabase } from '../lib/supabase';
import api from './api';




interface SignupData {
    email: string;
    password: string;
    inviteCode: string;
}

interface LoginData {
    email: string;
    password: string;
}

export const authService = {
    signup: async (data: SignupData) => {
        try {
            const response = await api.post('/auth/signup', data);
            return response.data;
        } catch (error: any) {
            if (error.response) {
                throw new Error(error.response.data.message || 'Signup failed');
            }
            throw new Error(error.message || 'An error occurred during signup');
        }
    },

    login: async (data: LoginData) => {
        try {
            const supabase = getSupabase();
            const { data: authData, error } = await supabase.auth.signInWithPassword({
                email: data.email,
                password: data.password,
            });

            if (error) throw error;
            if (!authData.session) throw new Error('No session returned');
            if (!authData.user?.id) throw new Error('No user ID returned');

            // Get user profile from our backend
            try {
                const response = await api.get(`/users/profile`, {
                    headers: {
                        Authorization: `Bearer ${authData.session.access_token}`
                    }
                });
                
                // Log successful login asynchronously
                void api.post('/log', {
                    message: 'user_login_success',
                    level: 'info',
                    meta: {
                        userId: response.data.id,
                        email: response.data.email,
                        role: response.data.role,
                        orgId: response.data.org_id,
                        userAgent: navigator.userAgent
                    }
                });

                // Update last signin timestamp
                void authService.updateLastSignin();

                return {
                    user: response.data,
                    session: authData.session
                };
            } catch (profileError: any) {
                console.error('Profile fetch error:', profileError);
                
                // Update last signin timestamp even if profile fetch fails
                void authService.updateLastSignin();
                
                // If profile fetch fails, still return the session
                return {
                    user: {
                        id: authData.user.id,
                        email: authData.user.email,
                        role: authData.user.user_metadata?.role || 'employee',
                    },
                    session: authData.session
                };
            }
        } catch (error: any) {
            console.error('Login error:', error);
            
            // Log failed login attempt
            void api.post('/log', {
                message: 'login_failed',
                level: 'error',
                meta: {
                    email: data.email,
                    error: error.message || 'Unknown error',
                    userAgent: navigator.userAgent
                }
            });

            if (error.response) {
                throw new Error(error.response.data.message || 'Login failed');
            }
            throw new Error(error.message);
        }
    },

    logout: async () => {
        try {
            const supabase = getSupabase();
            await supabase.auth.signOut();
        } catch (error: any) {
            throw new Error(error.message || 'An error occurred during logout');
        }
    },

    getSession: async () => {
        try {
            const supabase = getSupabase();
            const { data: { session }, error } = await supabase.auth.getSession();
            if (error) throw error;
            return session;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to get session');
        }
    },

    getUser: async () => {
        try {
            const supabase = getSupabase();
            const { data: { user }, error } = await supabase.auth.getUser();
            if (error) throw error;
            return user;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to get user');
        }
    },

    getProfile: async () => {
        try {
            const session = await authService.getSession();
            if (!session) throw new Error('No active session');

            const response = await api.get('users/profile', {
                headers: {
                    Authorization: `Bearer ${session.access_token}`
                }
            });
            return response.data;
        } catch (error: any) {
            console.error('Profile fetch error:', error);
            throw new Error(error.message || 'Failed to get profile');
        }
    },

    sendResetPasswordEmail: async (email: string) => {
        try {
            const response = await api.post('auth/reset-password/confirm', { email });
            return response.data;
        } catch (error: any) {
            if (error.response) {
                throw new Error(error.response.data.message || 'Failed to send password reset email');
            }
            throw new Error(error.message || 'An error occurred during password reset');
        }
    },

    verifyResetToken: async (token: string) => {
        try {
            const response = await api.get(`auth/reset-password/verify?token=${token}`);
            return response.data;
        } catch (error: any) {
            if (error.response) {
                throw new Error(error.response.data.message || 'Invalid or expired reset token');
            }
            throw new Error(error.message || 'An error occurred during token verification');
        }
    },

    resetPassword: async (token: string, password: string) => {
        try {
            const response = await api.post('auth/reset-password', { token, password });
            return response.data;
        } catch (error: any) {
            if (error.response) {
                throw new Error(error.response.data.message || 'Failed to reset password');
            }
            throw new Error(error.message || 'An error occurred during password reset');
        }
    },

    updateLastSignin: async () => {
        try {
            const response = await api.post('/auth/update-last-signin');
            return response.data;
        } catch (error: any) {
            // Don't throw error for this - it's not critical if it fails
            console.warn('Failed to update last signin:', error.message);
            return null;
        }
    }
}; 